if (!customElements.get('trumoto-home-story')) {
  customElements.define('trumoto-home-story', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.trumoto-home-story__track');
      this.slides = [...this.querySelectorAll('[data-story-slide]')];
      this.previous = this.querySelector('[data-story-previous]');
      this.next = this.querySelector('[data-story-next]');
      this.counter = this.querySelector('[data-story-current]');
      this.footer = this.querySelector('.trumoto-home-story__footer');
      this.galaxy = this.querySelector('[data-story-slide][data-intro-state]');
      this.film = this.galaxy?.querySelector('trumoto-intro-film');
      if (!this.track || !this.slides.length || !this.previous || !this.next || !this.counter) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.index = 0;
      this.frame = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.portrait = matchMedia('(max-width: 749px) and (orientation: portrait)');
      this.mobileLayout = matchMedia('(max-width: 749px), (max-width: 1024px) and (max-height: 600px)');
      this.videoControls = new Map();
      this.visibleVideos = new Set();
      this.media = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.visibleVideos.add(entry.target);
          else this.visibleVideos.delete(entry.target);
        });
        this.syncVideos();
      }, { threshold: .25 });
      this.querySelectorAll('video').forEach(video => {
        this.media.observe(video);
        if (video.closest('trumoto-intro-film')) return;
        video.muted = video.defaultMuted;
        this.videoControls.set(video, new window.TruMotoVideoControls(video, options));
      });
      this.motion.addEventListener('change', () => this.syncVideos(), options);
      this.portrait.addEventListener('change', () => this.syncVideos(), options);
      document.addEventListener('visibilitychange', () => this.syncVideos(), options);
      this.previous.addEventListener('click', () => this.go(this.index - 1), options);
      this.next.addEventListener('click', () => this.go(this.index + 1), options);
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track) return;
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const moves = { ArrowRight: this.index + (rtl ? -1 : 1), ArrowLeft: this.index + (rtl ? 1 : -1), Home: 0, End: this.slides.length - 1 };
        if (!(event.key in moves)) return;
        event.preventDefault();
        this.go(moves[event.key]);
      }, options);
      this.resize = new ResizeObserver(() => {
        this.go(this.index, false);
        if (this.footer) this.style.setProperty('--story-footer-height', `${this.footer.offsetHeight}px`);
      });
      this.resize.observe(this.track);
      if (this.footer) this.resize.observe(this.footer);
      this.watchIntroFooter(options);
      this.snapLatched = false;
      window.addEventListener('scroll', () => this.scheduleStorySnap(), { ...options, passive: true });
      window.addEventListener('touchstart', () => {
        this.snapTouching = true;
        clearTimeout(this.snapTimer);
      }, { ...options, passive: true });
      const releaseTouch = () => { this.snapTouching = false; this.scheduleStorySnap(); };
      window.addEventListener('touchend', releaseTouch, { ...options, passive: true });
      window.addEventListener('touchcancel', releaseTouch, { ...options, passive: true });
      this.setAttribute('data-enhanced', '');
      this.update();
    }

    // Phones play the Galaxy intro full screen: the footer stays hidden until a
    // tap on the video reveals its controls, then returns when the intro ends.
    watchIntroFooter(options) {
      if (!this.footer || !this.galaxy || !this.film) return;
      this.introObserver = new MutationObserver(() => this.syncIntroFooter());
      this.introObserver.observe(this.galaxy, { attributes: true, attributeFilter: ['data-intro-state'] });
      this.introObserver.observe(this.film, { attributes: true, attributeFilter: ['data-controls-visible'] });
      // Keep the footer up while it is pressed; the film hides its controls on any outside tap.
      this.footer.addEventListener('pointerdown', () => {
        clearTimeout(this.footerTimer);
        this.footerHeld = true;
        this.syncIntroFooter();
      }, options);
      const release = () => {
        if (!this.footerHeld) return;
        clearTimeout(this.footerTimer);
        this.footerTimer = setTimeout(() => { this.footerHeld = false; this.syncIntroFooter(); }, 600);
      };
      document.addEventListener('pointerup', release, options);
      document.addEventListener('pointercancel', release, options);
      this.footer.addEventListener('focusin', () => { this.footerFocused = true; this.syncIntroFooter(); }, options);
      this.footer.addEventListener('focusout', event => {
        if (event.relatedTarget && this.footer.contains(event.relatedTarget)) return;
        this.footerFocused = false;
        this.syncIntroFooter();
      }, options);
    }

    syncIntroFooter() {
      if (!this.galaxy) return;
      const onIntro = this.slides[this.index] === this.galaxy;
      const playing = this.galaxy.dataset.introState === 'playing';
      const revealed = this.film?.hasAttribute('data-controls-visible') || this.footerHeld || this.footerFocused;
      this.toggleAttribute('data-story-intro', onIntro);
      this.toggleAttribute('data-story-immersive', onIntro && playing && !revealed);
    }

    scheduleStorySnap() {
      clearTimeout(this.snapTimer);
      if (!this.mobileLayout.matches || this.motion.matches || this.snapTouching) return;
      this.snapTimer = setTimeout(() => this.settleStory(), 220);
    }

    settleStory() {
      if (!this.isConnected || !this.mobileLayout.matches || this.motion.matches || this.snapTouching || document.hidden) return;
      const bounds = this.getBoundingClientRect();
      const distance = bounds.top;
      if (Math.abs(distance) > 90) this.snapLatched = false;
      if (this.snapLatched || bounds.height > innerHeight + 4 || Math.abs(distance) > Math.min(28, innerHeight * .04)) return;
      this.snapLatched = true;
      if (Math.abs(distance) < 4) return;
      window.scrollTo({ top: Math.max(0, scrollY + distance), behavior: 'smooth' });
    }

    go(index, smooth = true) {
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      const direction = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollTo({ left: direction * target * this.track.clientWidth, behavior: smooth && !this.motion.matches ? 'smooth' : 'auto' });
    }

    update() {
      const width = this.track.clientWidth;
      if (!width) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, Math.round(Math.abs(this.track.scrollLeft) / width)));
      this.previous.disabled = this.index === 0;
      this.next.disabled = this.index === this.slides.length - 1;
      this.counter.textContent = String(this.index + 1);
      if (this.index > 0) this.setAttribute('data-swipe-discovered', '');
      this.syncIntroFooter();
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        if (!active) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      this.syncVideos();
    }

    syncVideos() {
      this.slides.forEach((slide, index) => slide.querySelectorAll('video').forEach(video => {
        const visible = index === this.index && this.visibleVideos.has(video) && !document.hidden;
        if (!visible) {
          video.pause();
          this.videoControls.get(video)?.hide();
          video.closest('trumoto-intro-film')?.controls?.hide();
          return;
        }
        const source = video.querySelector('source');
        const src = video.dataset.portraitSrc ? (this.portrait.matches ? video.dataset.portraitSrc : video.dataset.landscapeSrc) : source?.dataset.src;
        if (video.dataset.portraitSrc) video.poster = this.portrait.matches ? video.dataset.portraitPoster : video.dataset.landscapePoster;
        if (source && src && source.getAttribute('src') !== src) {
          source.src = src;
          source.removeAttribute('data-src');
          video.load();
        }
        if (!this.motion.matches && !video.hasAttribute('data-intro-complete')) video.play().catch(error => {
          if (error.name === 'AbortError') return;
          video.setAttribute('data-intro-blocked', '');
          video.closest('trumoto-intro-film')?.playFailed?.();
        });
        else video.pause();
      }));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.introObserver?.disconnect();
      clearTimeout(this.footerTimer);
      this.media?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
      cancelAnimationFrame(this.frame);
      clearTimeout(this.snapTimer);
      this.controller = null;
    }
  });
}
